/**
 * BackupCenter · v1.5.14
 * ─────────────────────────────────────────────────────────────────────────────
 * UX professionale per gestione backup, ispirata agli standard di Microsoft 365
 * Admin, Synology DSM, Veeam:
 *   1. Dashboard header con card "Ultimo backup" + statistiche aggregate.
 *   2. Toolbar azioni: ricerca istantanea, filtri temporali chip, tipo,
 *      "Crea backup ora" primario.
 *   3. Tabella paginata con ordinamento, badge tipo (manuale/auto/pre-ripristino),
 *      star preferiti, dimensione, data.
 *   4. Pannello Retention: politica di conservazione automatica.
 *   5. Sezione Ripristino separata (drag-drop upload + doppia conferma).
 *
 * NON modifica la logica di creazione/copia file: usa gli endpoint esistenti
 * `POST /admin/backup` e `POST /admin/restore` invariati.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";
import { format, isToday, isThisWeek, isThisMonth, isThisYear, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import {
  DatabaseBackup, Download, Search, Star, StarOff, ShieldAlert, Clock,
  Upload, RotateCcw, Filter, ChevronLeft, ChevronRight, ArrowUpDown, HardDrive,
} from "lucide-react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

const TYPE_LABEL = { manual: "Manuale", auto: "Automatico", pre_restore: "Pre-ripristino" };
const TYPE_VARIANT = { manual: "default", auto: "secondary", pre_restore: "outline" };
const PAGE_SIZE = 15;
const TIME_FILTERS = [
  { key: "all", label: "Tutti", test: () => true },
  { key: "today", label: "Oggi", test: (d) => isToday(d) },
  { key: "week", label: "Settimana", test: (d) => isThisWeek(d, { weekStartsOn: 1 }) },
  { key: "month", label: "Mese", test: (d) => isThisMonth(d) },
  { key: "year", label: "Anno", test: (d) => isThisYear(d) },
];

export default function BackupCenter() {
  const [backups, setBackups] = useState([]);
  const [backupDir, setBackupDir] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [timeFilter, setTimeFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [retention, setRetention] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreStep, setRestoreStep] = useState(0);
  const [restoreBusy, setRestoreBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/backups");
      setBackups(data.backups || []);
      setBackupDir(data.directory || "");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadRetention = async () => {
    try {
      const { data } = await api.get("/admin/backups/retention");
      setRetention(data);
    } catch (e) { /* silente */ }
  };
  useEffect(() => { load(); loadRetention(); }, []);

  const createBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backup");
      toast.success(`Backup creato: ${data.filename || "OK"}`);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const toggleStar = async (filename) => {
    try {
      const { data } = await api.post(`/admin/backups/${encodeURIComponent(filename)}/star`);
      setBackups((prev) => prev.map((b) => b.filename === filename ? { ...b, starred: data.starred } : b));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const download = (filename) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${base}/api/admin/backup/${encodeURIComponent(filename)}/download`, "_blank");
  };
  const saveRetention = async (patch) => {
    try {
      const { data } = await api.patch("/admin/backups/retention", { ...retention, ...patch });
      setRetention(data);
      toast.success("Politica retention aggiornata");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doRestore = async () => {
    if (!restoreFile) return;
    setRestoreBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ripristino completato. Sarà necessario rieffettuare il login.");
      setRestoreFile(null);
      setRestoreStep(0);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRestoreBusy(false); }
  };

  // Aggregazioni per l'header
  const lastBackup = backups[0] || null;
  const stats = useMemo(() => ({
    total: backups.length,
    starred: backups.filter((b) => b.starred).length,
    manual: backups.filter((b) => b.type === "manual").length,
    auto: backups.filter((b) => b.type === "auto").length,
    pre_restore: backups.filter((b) => b.type === "pre_restore").length,
    total_mb: backups.reduce((a, b) => a + (b.size_mb || 0), 0),
  }), [backups]);

  // Filtro + ricerca + ordinamento
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const timeTest = TIME_FILTERS.find((f) => f.key === timeFilter)?.test;
    return backups
      .filter((b) => !q || b.filename.toLowerCase().includes(q))
      .filter((b) => typeFilter === "all" || b.type === typeFilter)
      .filter((b) => !timeTest || timeTest(parseISO(b.created_at)))
      .sort((a, b) => {
        const va = a[sort.key], vb = b[sort.key];
        const cmp = va < vb ? -1 : va > vb ? 1 : 0;
        return sort.dir === "asc" ? cmp : -cmp;
      });
  }, [backups, search, typeFilter, timeFilter, sort]);

  // Paginazione
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const toggleSort = (key) => setSort((s) => s.key === key
    ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key, dir: "desc" });

  return (
    <div className="space-y-6" data-testid="backup-center">
      {/* ══════════ DASHBOARD HEADER ══════════ */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="backup-last-card" className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2"><DatabaseBackup className="h-4 w-4"/>Ultimo backup</CardDescription>
            {lastBackup ? (
              <CardTitle className="text-2xl">
                {format(parseISO(lastBackup.created_at), "d MMMM yyyy · HH:mm", { locale: it })}
              </CardTitle>
            ) : (
              <CardTitle className="text-2xl text-muted-foreground">Nessun backup presente</CardTitle>
            )}
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-1">
            {lastBackup && (<>
              <div className="font-mono text-xs truncate">{lastBackup.filename}</div>
              <div>{lastBackup.size_mb} MB · <Badge variant={TYPE_VARIANT[lastBackup.type]}>{TYPE_LABEL[lastBackup.type]}</Badge></div>
            </>)}
            <div className="pt-2">
              <Button onClick={createBackup} disabled={busy} data-testid="backup-create-btn" className="!bg-suite-primary !text-white hover:!bg-suite-primary/90">
                <DatabaseBackup className="h-4 w-4 mr-2"/> Crea backup ora
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="backup-stats-card">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2"><HardDrive className="h-4 w-4"/>Archivio</CardDescription>
            <CardTitle className="text-2xl">{stats.total}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-1">
            <div>Spazio: <b>{stats.total_mb.toFixed(1)} MB</b></div>
            <div className="flex flex-wrap gap-1 pt-1">
              <Badge variant="default">{stats.manual} manuali</Badge>
              <Badge variant="secondary">{stats.auto} auto</Badge>
              <Badge variant="outline">{stats.pre_restore} pre-ripristino</Badge>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="backup-retention-card">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2"><Clock className="h-4 w-4"/>Retention</CardDescription>
            <CardTitle className="text-2xl">{retention?.enabled ? `${retention?.keep_days} gg` : "OFF"}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <div className="flex items-center gap-2">
              <Switch checked={!!retention?.enabled} onCheckedChange={(v) => saveRetention({ enabled: v })} data-testid="retention-toggle"/>
              <Label className="text-xs">Attiva pulizia automatica</Label>
            </div>
            {retention?.enabled && (
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <Label>Giorni</Label>
                  <Input type="number" min={7} max={3650} value={retention?.keep_days || 90}
                    onChange={(e) => setRetention((r) => ({ ...r, keep_days: Number(e.target.value) }))}
                    onBlur={() => saveRetention({ keep_days: retention?.keep_days })}
                    className="h-7 w-20" data-testid="retention-days"/>
                </div>
                <div className="flex items-center gap-2">
                  <Label>Minimo n.</Label>
                  <Input type="number" min={0} max={999} value={retention?.keep_min_count || 10}
                    onChange={(e) => setRetention((r) => ({ ...r, keep_min_count: Number(e.target.value) }))}
                    onBlur={() => saveRetention({ keep_min_count: retention?.keep_min_count })}
                    className="h-7 w-20" data-testid="retention-min"/>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Switch checked={!!retention?.keep_starred_always}
                    onCheckedChange={(v) => saveRetention({ keep_starred_always: v })}/>
                  <Label className="text-xs">Preserva preferiti</Label>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ══════════ TOOLBAR RICERCA/FILTRI ══════════ */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
          <Input placeholder="Cerca per nome…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8 w-72" data-testid="backup-search"/>
        </div>
        <div className="flex items-center gap-1" data-testid="backup-time-filter">
          {TIME_FILTERS.map((f) => (
            <Button key={f.key} variant={timeFilter === f.key ? "default" : "outline"} size="sm"
              onClick={() => { setTimeFilter(f.key); setPage(1); }}
              data-testid={`time-filter-${f.key}`}>
              {f.label}
            </Button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Filter className="h-4 w-4 text-muted-foreground"/>
          {["all", "manual", "auto", "pre_restore"].map((t) => (
            <Button key={t} variant={typeFilter === t ? "default" : "outline"} size="sm"
              onClick={() => { setTypeFilter(t); setPage(1); }}
              data-testid={`type-filter-${t}`}>
              {t === "all" ? "Tutti" : TYPE_LABEL[t]}
            </Button>
          ))}
        </div>
      </div>

      {/* ══════════ TABELLA PAGINATA ══════════ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Elenco backup ({filtered.length})</CardTitle>
          <CardDescription className="font-mono text-xs">{backupDir}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table data-testid="backup-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="cursor-pointer" onClick={() => toggleSort("created_at")}>
                  Data <ArrowUpDown className="inline h-3 w-3"/>
                </TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Nome file</TableHead>
                <TableHead className="cursor-pointer text-right" onClick={() => toggleSort("size_mb")}>
                  Dimensione <ArrowUpDown className="inline h-3 w-3"/>
                </TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Nessun backup corrisponde ai filtri.
                </TableCell></TableRow>
              ) : paged.map((b) => (
                <TableRow key={b.filename} data-testid={`backup-row-${b.filename}`}>
                  <TableCell>
                    <button onClick={() => toggleStar(b.filename)} data-testid={`star-${b.filename}`} aria-label="preferito">
                      {b.starred
                        ? <Star className="h-4 w-4 text-amber-500 fill-amber-500"/>
                        : <StarOff className="h-4 w-4 text-muted-foreground"/>}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium">
                    {format(parseISO(b.created_at), "d MMM yyyy · HH:mm", { locale: it })}
                  </TableCell>
                  <TableCell>
                    <Badge variant={TYPE_VARIANT[b.type]}>{TYPE_LABEL[b.type]}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs truncate max-w-md">{b.filename}</TableCell>
                  <TableCell className="text-right">{b.size_mb} MB</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => download(b.filename)}
                      data-testid={`download-${b.filename}`}>
                      <Download className="h-4 w-4"/>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pageCount > 1 && (
            <div className="flex items-center justify-end gap-2 pt-3">
              <Button variant="outline" size="sm" disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="backup-page-prev">
                <ChevronLeft className="h-4 w-4"/>
              </Button>
              <span className="text-xs">Pagina {currentPage} / {pageCount}</span>
              <Button variant="outline" size="sm" disabled={currentPage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))} data-testid="backup-page-next">
                <ChevronRight className="h-4 w-4"/>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ══════════ SEZIONE RIPRISTINO (SEPARATA) ══════════ */}
      <Card className="border-suite-error/40" data-testid="restore-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-suite-error">
            <ShieldAlert className="h-5 w-5"/> Ripristino database
          </CardTitle>
          <CardDescription>
            Il ripristino sostituisce l'intero database attuale. Verrà creato un backup automatico
            pre-ripristino. Operazione riservata all'amministratore di sistema.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <label htmlFor="restore-file" className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-suite-neutral-300 rounded-md p-4 text-center hover:bg-suite-neutral-50">
                <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground"/>
                <div className="text-sm">
                  {restoreFile ? <span className="font-medium">{restoreFile.name}</span>
                    : "Trascina o seleziona un file .db"}
                </div>
              </div>
              <input id="restore-file" type="file" accept=".db,.sqlite,.sqlite3" className="hidden"
                onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                data-testid="restore-file-input"/>
            </label>
            <Button variant="destructive" disabled={!restoreFile || restoreBusy}
              onClick={() => setRestoreStep(1)} data-testid="restore-btn">
              <RotateCcw className="h-4 w-4 mr-2"/> Avvia ripristino
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={restoreStep > 0} onOpenChange={(v) => !v && setRestoreStep(0)}>
        <DialogContent data-testid="restore-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-suite-error">
              <ShieldAlert className="h-5 w-5"/> Confermi il ripristino?
            </DialogTitle>
            <DialogDescription>
              Il database attuale sarà sostituito con quello caricato. Verrà creato
              automaticamente un backup "pre-ripristino" a scopo di sicurezza.
              Tutti gli utenti attivi verranno disconnessi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreStep(0)} data-testid="restore-cancel">
              Annulla
            </Button>
            <Button variant="destructive" onClick={doRestore} disabled={restoreBusy}
              data-testid="restore-confirm">
              {restoreBusy ? "Ripristino in corso…" : "Sì, procedi con il ripristino"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
